import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Play, Pause, Volume2, VolumeX, Heart, Music4, Loader2, Megaphone, ExternalLink, Music2, ListMusic, Bookmark, Share2, Moon } from "lucide-react";
import { usePlayer } from "@/context/PlayerContext";
import { useAuth } from "@/context/AuthContext";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import RequestDialog from "@/components/RequestDialog";

export default function BottomPlayer() {
  const { current, playing, loading, volume, setVolume, muted, setMuted, error, isAd, ad, reconnecting, sleepAt, setSleepTimer, togglePlay } = usePlayer();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [fav, setFav] = useState(false);
  const [reqOpen, setReqOpen] = useState(false);
  const [nowPlaying, setNowPlaying] = useState(null);
  const [songSaved, setSongSaved] = useState(false);
  useEffect(() => { setSongSaved(false); }, [nowPlaying?.title]);
  const [trackMenu, setTrackMenu] = useState(false);
  const [history, setHistory] = useState([]);
  const [sleepMenu, setSleepMenu] = useState(false);
  const [sleepLeft, setSleepLeft] = useState("");

  useEffect(() => {
    if (!sleepAt) { setSleepLeft(""); return; }
    const tick = () => {
      const ms = sleepAt - Date.now();
      if (ms <= 0) { setSleepLeft(""); return; }
      const m = Math.floor(ms / 60000), s = Math.floor((ms % 60000) / 1000);
      setSleepLeft(`${m}:${String(s).padStart(2, "0")}`);
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [sleepAt]);

  useEffect(() => {
    setNowPlaying(null);
    if (!current || isAd) return;
    let active = true;
    const fetchNow = () => {
      api.get(`/stations/${current.id}/nowplaying`).then((res) => {
        if (active) setNowPlaying(res.data?.title ? res.data : null);
      }).catch(() => {});
    };
    fetchNow();
    const t = setInterval(fetchNow, 20000);
    return () => { active = false; clearInterval(t); };
  }, [current, isAd]);

  // Feed track info + album art to the phone lock screen / OS media controls.
  useEffect(() => {
    if (!("mediaSession" in navigator)) return;
    if (!current || isAd) { try { navigator.mediaSession.metadata = null; } catch (e) {} return; }
    try {
      navigator.mediaSession.metadata = new window.MediaMetadata({
        title: nowPlaying?.song || current.name,
        artist: nowPlaying?.artist || current.genre || "In His Image Radio",
        album: current.name,
        artwork: [{ src: nowPlaying?.artwork || current.image_url, sizes: "512x512", type: "image/jpeg" }],
      });
      navigator.mediaSession.setActionHandler("play", () => { if (!playing) togglePlay(); });
      navigator.mediaSession.setActionHandler("pause", () => { if (playing) togglePlay(); });
      navigator.mediaSession.playbackState = playing ? "playing" : "paused";
    } catch (e) {}
  }, [current, isAd, nowPlaying, playing, togglePlay]);

  const openTrackMenu = () => {
    setTrackMenu((v) => !v);
    if (current) api.get(`/stations/${current.id}/history?limit=6`).then((r) => setHistory(r.data || [])).catch(() => {});
  };
  const saveSong = async () => {
    if (!user) { navigate("/login"); return; }
    try {
      await api.post("/saved-songs", { song: nowPlaying?.song, artist: nowPlaying?.artist, artwork: nowPlaying?.artwork, station_name: current?.name });
      toast.success("Saved to your songs");
      setSongSaved(true);
      setTrackMenu(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
  };
  const heartSong = async (e) => {
    e.stopPropagation();
    if (!user) { navigate("/login"); return; }
    if (songSaved) { toast.success("Already in your Saved Songs"); return; }
    try {
      await api.post("/saved-songs", { song: nowPlaying?.song, artist: nowPlaying?.artist, artwork: nowPlaying?.artwork, station_name: current?.name });
      setSongSaved(true);
      toast.success("Saved to your songs ❤", { action: { label: "View", onClick: () => navigate("/favorites") } });
    } catch (err) { toast.error(err.response?.data?.detail || "Could not save"); }
  };
  const shareSong = async () => {
    const track = `${nowPlaying?.song}${nowPlaying?.artist ? ` by ${nowPlaying.artist}` : ""}`;
    const text = `🎵 Now playing: ${track} on ${current?.name} — In His Image Radio`;
    const url = window.location.origin;
    try {
      if (navigator.share) {
        await navigator.share({ title: "In His Image Radio", text, url });
      } else {
        await navigator.clipboard.writeText(`${text} ${url}`);
        toast.success("Copied — share it with friends!");
      }
      setTrackMenu(false);
    } catch (e) { /* user cancelled share */ }
  };

  useEffect(() => {
    if (!user || !playing || !current || isAd) return;
    const beat = () => api.post("/listening/heartbeat", { kind: "station", station_id: current.id, station_name: current.name }).catch(() => {});
    beat();
    const t = setInterval(beat, 20000);
    return () => clearInterval(t);
  }, [user, playing, current, isAd]);

  if (!current) return null;

  const toggleFav = async () => {
    if (!user) { navigate("/login"); return; }
    try {
      const { data } = await api.post(`/favorites/${current.id}`);
      setFav(data.favorite);
      toast.success(data.favorite ? "Added to favorites" : "Removed from favorites");
    } catch (e) {
      toast.error("Could not update favorite");
    }
  };

  return (
    <>
      <div className={`fixed ${user ? "bottom-16" : "bottom-0"} lg:bottom-0 left-0 right-0 z-50 glass border-t`} data-testid="bottom-player" style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
        <div className="max-w-[1600px] mx-auto px-4 py-3 lg:pl-6 grid grid-cols-2 lg:grid-cols-3 items-center gap-4 mb-14 lg:mb-0">
          {/* Left: art + info */}
          <div className="flex items-center gap-3 min-w-0 relative">
            <div className={`relative w-14 h-14 rounded-xl overflow-hidden shrink-0 ${isAd ? "shadow-[0_0_24px_rgba(91,124,250,0.35)]" : "shadow-[0_0_24px_rgba(230,179,74,0.18)]"}`}>
              <img
                src={isAd && ad?.has_logo ? `${API}/spots/${ad.id}/logo` : (!isAd && nowPlaying?.artwork ? nowPlaying.artwork : current.image_url)}
                onError={(e) => { if (!isAd && current.image_url && e.target.src !== current.image_url) e.target.src = current.image_url; }}
                alt={isAd ? ad?.business_name : (nowPlaying?.song || current.name)}
                data-testid="player-art"
                className={`w-full h-full object-cover transition-opacity duration-500 ${isAd && !ad?.has_logo ? "opacity-40" : ""}`}
              />
              {!isAd && nowPlaying?.artwork && <span className="absolute bottom-0.5 right-0.5 bg-[#0A1030]/80 rounded-md p-0.5"><Music2 className="w-2.5 h-2.5 text-[#E6B34A]" /></span>}
              {isAd && !ad?.has_logo && <div className="absolute inset-0 flex items-center justify-center"><Megaphone className="w-6 h-6 text-[#AFC0FF]" /></div>}
            </div>
            <div className="min-w-0" data-testid="player-info">
              {isAd ? (
                <>
                  <p className="overline text-[#AFC0FF]" data-testid="player-ad-label">Advertisement</p>
                  <p className="font-display font-bold text-sm truncate">{ad?.business_name}</p>
                  {ad?.link_url ? (
                    <a href={ad.link_url} target="_blank" rel="noopener noreferrer" data-testid="ad-link" className="inline-flex items-center gap-1 text-xs text-[#E6B34A] hover:text-[#CF9A34] transition-colors">
                      Visit sponsor <ExternalLink className="w-3 h-3" />
                    </a>
                  ) : (
                    <p className="text-xs text-zinc-500 truncate">Back to {current.name} shortly…</p>
                  )}
                </>
              ) : (
                <>
                  <p className="font-display font-bold text-sm truncate" data-testid="player-station-name">{current.name}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    {reconnecting ? (
                      <span className="flex items-center gap-1.5 text-[#E6B34A] shrink-0" data-testid="reconnecting-indicator">
                        <Loader2 className="w-3 h-3 animate-spin" /> <span className="overline">Reconnecting…</span>
                      </span>
                    ) : playing && !loading ? (
                      <span className="flex items-center gap-1 text-[#FF3B30] shrink-0">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#FF3B30] live-dot" /> <span className="overline">Live</span>
                      </span>
                    ) : (
                      <span className="text-xs text-zinc-500 truncate">{current.genre}</span>
                    )}
                    {nowPlaying?.title && !reconnecting && (
                      <button onClick={openTrackMenu} data-testid="now-playing" className="flex items-center gap-1 text-xs text-zinc-400 hover:text-white min-w-0 max-w-[220px] transition-colors">
                        <Music2 className="w-3 h-3 text-[#E6B34A] shrink-0" />
                        <span className="ticker-mask flex-1 min-w-0">
                          <span className="ticker-text">{nowPlaying.song}{nowPlaying.artist ? ` — ${nowPlaying.artist}` : ""}</span>
                        </span>
                      </button>
                    )}
                    {nowPlaying?.title && !reconnecting && (
                      <button onClick={heartSong} data-testid="nowplaying-heart" aria-label="Save this song" title="Save this song" className="shrink-0 text-zinc-400 hover:text-[#FF3B30] transition-colors">
                        <Heart className={`w-3.5 h-3.5 ${songSaved ? "fill-[#FF3B30] text-[#FF3B30]" : ""}`} />
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
            {trackMenu && nowPlaying?.title && !isAd && (
              <>
                <div className="fixed inset-0 z-[60]" onClick={() => setTrackMenu(false)} data-testid="track-menu-overlay" />
                <div className="absolute bottom-full left-2 mb-3 w-72 max-w-[85vw] rounded-2xl border border-white/10 bg-[#141C42] shadow-2xl p-3 z-[61]" data-testid="track-menu">
                  <div className="flex items-center gap-3 pb-3 border-b border-white/5">
                    <img src={nowPlaying.artwork || current.image_url} alt="" className="w-12 h-12 rounded-lg object-cover shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-bold truncate" data-testid="track-menu-song">{nowPlaying.song}</p>
                      {nowPlaying.artist && <p className="text-xs text-zinc-400 truncate">{nowPlaying.artist}</p>}
                    </div>
                  </div>
                  <div className="flex gap-2 py-3">
                    <button onClick={() => { setReqOpen(true); setTrackMenu(false); }} data-testid="track-request" className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl bg-[#E6B34A] text-[#0A1030] text-xs font-bold hover:bg-[#CF9A34] transition-colors"><Music4 className="w-3.5 h-3.5" /> Request</button>
                    <button onClick={saveSong} data-testid="track-save" className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl bg-white/5 hover:bg-white/10 text-xs font-bold transition-colors"><Bookmark className="w-3.5 h-3.5" /> Save</button>
                    <button onClick={shareSong} data-testid="track-share" className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl bg-white/5 hover:bg-white/10 text-xs font-bold transition-colors"><Share2 className="w-3.5 h-3.5" /> Share</button>
                  </div>
                  {history.length > 0 && (
                    <div className="pt-1">
                      <p className="overline text-zinc-500 mb-2 flex items-center gap-1.5"><ListMusic className="w-3.5 h-3.5" /> Recently played</p>
                      <div className="space-y-1.5 max-h-52 overflow-y-auto no-scrollbar" data-testid="track-history">
                        {history.filter((h) => h.title !== nowPlaying.title).slice(0, 5).map((h, i) => (
                          <div key={i} className="flex items-center gap-2.5">
                            <img src={h.artwork || current.image_url} alt="" className="w-8 h-8 rounded-md object-cover shrink-0" />
                            <div className="min-w-0"><p className="text-xs font-semibold truncate">{h.song}</p>{h.artist && <p className="text-[10px] text-zinc-500 truncate">{h.artist}</p>}</div>
                          </div>
                        ))}
                        {history.filter((h) => h.title !== nowPlaying.title).length === 0 && <p className="text-[11px] text-zinc-600">No earlier tracks yet — check back soon.</p>}
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Center: controls */}
          <div className="flex items-center justify-end lg:justify-center gap-4">
            <div className="relative lg:hidden">
              <button onClick={() => setSleepMenu((v) => !v)} data-testid="sleep-timer-mobile" aria-label="Sleep timer" className={`flex items-center gap-1 transition-colors ${sleepAt ? "text-[#E6B34A]" : "text-zinc-400"}`}>
                <Moon className="w-5 h-5" />
                {sleepAt && <span className="text-[10px] font-mono font-bold tabular-nums">{sleepLeft}</span>}
              </button>
              {sleepMenu && (
                <>
                  <div className="fixed inset-0 z-[60]" onClick={() => setSleepMenu(false)} />
                  <div className="absolute bottom-full left-0 mb-3 w-44 rounded-2xl border border-white/10 bg-[#141C42] shadow-2xl p-2 z-[61]" data-testid="sleep-menu-mobile">
                    <p className="overline text-[#E6B34A] px-2 py-1 flex items-center gap-1.5"><Moon className="w-3.5 h-3.5" /> Sleep timer</p>
                    {[15, 30, 45, 60].map((m) => (
                      <button key={m} onClick={() => { setSleepTimer(m); setSleepMenu(false); toast.success(`Playback will stop in ${m} minutes`); }} data-testid={`sleep-m-${m}`} className="w-full text-left px-3 py-2 rounded-lg text-sm hover:bg-white/5 transition-colors">{m} minutes</button>
                    ))}
                    {sleepAt && (
                      <button onClick={() => { setSleepTimer(null); setSleepMenu(false); toast("Sleep timer off"); }} className="w-full text-left px-3 py-2 rounded-lg text-sm text-[#FF7A70] hover:bg-[#FF3B30]/10 transition-colors">Turn off</button>
                    )}
                  </div>
                </>
              )}
            </div>
            <button
              onClick={togglePlay}
              data-testid="player-play-toggle"
              aria-label={playing ? "Pause" : "Play"}
              className="w-12 h-12 rounded-full bg-[#E6B34A] hover:bg-[#CF9A34] text-[#0A1030] flex items-center justify-center transition-colors shadow-[0_0_24px_rgba(230,179,74,0.4)]"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : playing ? <Pause className="w-5 h-5" fill="currentColor" /> : <Play className="w-5 h-5 ml-0.5" fill="currentColor" />}
            </button>
          </div>

          {/* Right: actions (desktop) */}
          <div className="hidden lg:flex items-center justify-end gap-4">
            <button onClick={toggleFav} disabled={isAd} data-testid="player-fav-toggle" aria-label="Favorite" className="text-zinc-400 hover:text-[#FF3B30] transition-colors disabled:opacity-30">
              <Heart className={`w-5 h-5 ${fav ? "fill-[#FF3B30] text-[#FF3B30]" : ""}`} />
            </button>
            <button onClick={() => (user ? setReqOpen(true) : navigate("/login"))} disabled={isAd} data-testid="player-request" aria-label="Request song" className="text-zinc-400 hover:text-[#E6B34A] transition-colors disabled:opacity-30">
              <Music4 className="w-5 h-5" />
            </button>
            <div className="relative">
              <button onClick={() => setSleepMenu((v) => !v)} data-testid="sleep-timer" aria-label="Sleep timer" className={`flex items-center gap-1 transition-colors ${sleepAt ? "text-[#E6B34A]" : "text-zinc-400 hover:text-white"}`}>
                <Moon className="w-5 h-5" />
                {sleepAt && <span className="text-[10px] font-mono font-bold tabular-nums" data-testid="sleep-remaining">{sleepLeft}</span>}
              </button>
              {sleepMenu && (
                <>
                  <div className="fixed inset-0 z-[60]" onClick={() => setSleepMenu(false)} />
                  <div className="absolute bottom-full right-0 mb-3 w-44 rounded-2xl border border-white/10 bg-[#141C42] shadow-2xl p-2 z-[61]" data-testid="sleep-menu">
                    <p className="overline text-[#E6B34A] px-2 py-1 flex items-center gap-1.5"><Moon className="w-3.5 h-3.5" /> Sleep timer</p>
                    {[15, 30, 45, 60].map((m) => (
                      <button key={m} onClick={() => { setSleepTimer(m); setSleepMenu(false); toast.success(`Playback will stop in ${m} minutes`); }} data-testid={`sleep-${m}`} className="w-full text-left px-3 py-2 rounded-lg text-sm hover:bg-white/5 transition-colors">{m} minutes</button>
                    ))}
                    {sleepAt && (
                      <button onClick={() => { setSleepTimer(null); setSleepMenu(false); toast("Sleep timer off"); }} data-testid="sleep-off" className="w-full text-left px-3 py-2 rounded-lg text-sm text-[#FF7A70] hover:bg-[#FF3B30]/10 transition-colors">Turn off</button>
                    )}
                  </div>
                </>
              )}
            </div>
            <div className="flex items-center gap-2 w-36">
              <button onClick={() => setMuted((m) => !m)} aria-label="Mute" data-testid="player-mute" className="text-zinc-400 hover:text-white transition-colors">
                {muted || volume === 0 ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
              </button>
              <Slider value={[muted ? 0 : Math.round(volume * 100)]} max={100} step={1} onValueChange={(v) => { setVolume(v[0] / 100); if (v[0] > 0) setMuted(false); }} data-testid="player-volume" />
            </div>
          </div>
        </div>
        {error && <p className="text-center text-xs text-[#FF3B30] pb-2">{error}</p>}
      </div>
      <RequestDialog open={reqOpen} onOpenChange={setReqOpen} station={current} initialSong={nowPlaying?.song || ""} initialArtist={nowPlaying?.artist || ""} />
    </>
  );
}
